import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import LogoSettings from './LogoSettings';
import WebsiteSettings from './WebsiteSettings';
import SmtpSettings from './SmtpSettings';

const GeneralSettings = ({ user, onSettingsSaved }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [generalSettings, setGeneralSettings] = useState({
    siteTitle: 'SAP OCI Portal',
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    smtpFrom: '',
    smtpSecure: true,
    emailNotificationsEnabled: false,
    logo: null
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    loadGeneralSettings();
  }, []);

  const loadGeneralSettings = async () => {
    try {
      console.log('🔄 Loading general settings...');
      const response = await fetch('/api/admin/settings', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      if (response.ok) {
        const settings = await response.json();
        console.log('✅ Loaded settings from API:', settings);
        console.log('📧 Email notifications from API:', settings.emailNotificationsEnabled);
        console.log('🔑 API response keys:', Object.keys(settings));
        
        let logoValue = settings.logo;
        if (logoValue === 'null' || logoValue === null || logoValue === undefined || logoValue === '') {
          logoValue = null;
        }
        
        const newSettings = {
          siteTitle: settings.siteTitle || 'SAP OCI Portal',
          smtpHost: settings.smtpHost || '',
          smtpPort: settings.smtpPort || '587',
          smtpUser: settings.smtpUser || '',
          smtpPassword: settings.smtpPassword || '',
          smtpFrom: settings.smtpFrom || '',
          smtpSecure: settings.smtpSecure === true,
          emailNotificationsEnabled: settings.emailNotificationsEnabled === true,
          logo: logoValue
        };
        
        console.log('🔍 Email notifications from backend:', settings.emailNotificationsEnabled);
        console.log('🔍 Email notifications final value:', newSettings.emailNotificationsEnabled);
        console.log('🖼️ Logo value processed:', logoValue);
        
        console.log('🎯 Setting state to:', newSettings);
        console.log('📧 Email notifications final value:', newSettings.emailNotificationsEnabled);
        setGeneralSettings(newSettings);
      }
    } catch (error) {
      console.error('Error loading general settings:', error);
    }
  };

  const saveGeneralSettings = async () => {
    setSettingsLoading(true);
    setSettingsError('');
    setSettingsSaved(false);

    console.log('Saving settings:', generalSettings);
    console.log('Email notifications enabled:', generalSettings.emailNotificationsEnabled);
    console.log('JSON being sent:', JSON.stringify(generalSettings, null, 2));

    try {
      const response = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(generalSettings)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('admin.settings.saveError'));
      }

      console.log('✅ Settings saved successfully to backend');

      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
      
      if (generalSettings.siteTitle) {
        document.title = generalSettings.siteTitle;
      }

      if (onSettingsSaved) {
        onSettingsSaved(generalSettings.logo, generalSettings.siteTitle);
      }
      
      window.dispatchEvent(new CustomEvent('settingsUpdated', {
        detail: { logo: generalSettings.logo, siteTitle: generalSettings.siteTitle }
      }));

      setTimeout(() => {
        console.log('🔄 Reloading settings after save...');
        loadGeneralSettings();
      }, 1500);

      try {
        await fetch('/api/public/settings/refresh', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
      } catch (error) {
        console.error('Error refreshing public settings:', error);
      }
      
    } catch (error) {
      console.error('Settings save error:', error);
      setSettingsError(error.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  return (
    <div className="relative group">
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20'
        : 'glass-light-card border border-slate-200'
      } ${isDarkMode ? 'backdrop-blur-2xl' : ''} rounded-3xl ${isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'shadow-sm'}`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>

      <div className={`absolute bottom-8 left-12 w-1 h-1 ${isDarkMode ? 'bg-white/30' : 'hidden'} rounded-full animate-ping`}></div>
      
      <div className="relative p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('admin.settings.title')}</h3>
          <button
            onClick={saveGeneralSettings}
            disabled={settingsLoading}
            className={`relative group/save overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] shadow-sm'}`}
            style={isDarkMode ? {
              background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            } : {}}
          >
            <div
              className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/save:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
              } : {}}
            ></div>
                       
            <div className={`relative z-10 px-4 py-2 flex items-center font-medium transition-all duration-300 ${
              isDarkMode ? 'text-white drop-shadow-lg group-hover/save:drop-shadow-xl' : 'text-white'
            }`}>
              {settingsLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/80 border-t-transparent mr-2"></div>
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/save:scale-110" />
                  {t('common.save')}
                </>
              )}
            </div>
            
            <div className={`absolute inset-0 opacity-0 group-hover/save:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
              <div
                className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/save:translate-x-full transition-transform duration-800"
                style={isDarkMode ? {
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                } : {}}
              ></div>
            </div>
          </button>
        </div>

        {settingsError && (
          <div className="mb-4 relative">
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-red-500/10 border border-red-300/30 backdrop-blur-xl shadow-[0_8px_32px_rgba(239,68,68,0.2)]'
              : 'bg-red-50 border border-red-200 shadow-sm'
            } rounded-2xl`}></div>
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-r from-red-400/10 to-pink-400/10'
              : ''
            } rounded-2xl`}></div>
            <div className={`absolute top-0 left-0 w-full h-0.5 ${isDarkMode
              ? 'bg-gradient-to-r from-red-400/70 to-pink-400/70'
              : 'bg-red-400'
            } rounded-t-2xl`}></div>
            <div className="relative p-3">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-red-200 drop-shadow-sm' : 'text-red-700'}`}>{settingsError}</p>
            </div>
          </div>
        )}

        {settingsSaved && (
          <div className="mb-4 relative">
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-emerald-500/10 border border-emerald-300/30 backdrop-blur-xl shadow-[0_8px_32px_rgba(16,185,129,0.2)]'
              : 'bg-emerald-50 border border-emerald-200 shadow-sm'
            } rounded-2xl`}></div>
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-r from-emerald-400/10 to-green-400/10'
              : ''
            } rounded-2xl`}></div>
            <div className={`absolute top-0 left-0 w-full h-0.5 ${isDarkMode
              ? 'bg-gradient-to-r from-emerald-400/70 to-green-400/70'
              : 'bg-emerald-400'
            } rounded-t-2xl`}></div>
            <div className="relative p-3">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-emerald-200 drop-shadow-sm' : 'text-emerald-700'}`}>{t('admin.settings.saved')}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <LogoSettings 
            generalSettings={generalSettings}
            setGeneralSettings={setGeneralSettings}
          />
          
          <WebsiteSettings 
            generalSettings={generalSettings}
            setGeneralSettings={setGeneralSettings}
          />
          
          <SmtpSettings 
            generalSettings={generalSettings}
            setGeneralSettings={setGeneralSettings}
            user={user}
          />
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;